import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = [
    'group', 'sharedDelivery', 'addressInput', 'submitButton', 'aggregateTotal',
    'codWarning'
  ]

  static values = {
    codAllowed: Boolean,
    phonePresent: Boolean
  }

  connect () {
    this.sync()
  }

  sync () {
    let combinedTotal = 0
    let anyDelivery = false
    let configurationMissing = false
    let blockedByCod = false

    this.groupTargets.forEach((group) => {
      const fulfillmentInput = group.querySelector('input[data-role="fulfillment"]:checked')
      const fulfillment = fulfillmentInput && !fulfillmentInput.disabled ? fulfillmentInput.value : null
      const zone = this.selectedZone(group)
      const zoneBlocksCod = fulfillment === 'shop_delivery' &&
        zone === 'out_of_city' &&
        group.dataset.outOfCityCodAllowed !== 'true'
      const rulesReason = this.advancedCodRulesReason(group)
      const rulesBlockCod = rulesReason != null
      const riderBlocksCod = fulfillment === 'ebazzari_rider' && group.dataset.platformNewBuyer === 'true'
      const codBlocked = zoneBlocksCod || rulesBlockCod || riderBlocksCod

      this.syncGroupCod(group, codBlocked)
      this.syncGroupZone(group, fulfillment)

      const paymentInput = group.querySelector('input[data-role="payment"]:checked')
      const payment = paymentInput && !paymentInput.disabled ? paymentInput.value : null
      const subtotal = Number(group.dataset.subtotalCents || 0)
      const shopFee = Number(group.dataset.shopDeliveryFeeCents || 0)
      const freeThreshold = Number(group.dataset.freeDeliveryThresholdCents || 0)
      const riderFee = Number(group.dataset.riderFeeCents || 0)

      if (!fulfillment || !payment) configurationMissing = true
      if (payment === 'cod' && (!this.codAllowedValue || codBlocked)) blockedByCod = true

      const delivery = fulfillment === 'shop_delivery' || fulfillment === 'ebazzari_rider'
      anyDelivery ||= delivery
      let fee = 0
      if (fulfillment === 'ebazzari_rider') {
        fee = riderFee
      } else if (delivery) {
        fee = freeThreshold > 0 && subtotal >= freeThreshold ? 0 : shopFee
      }
      const orderTotal = subtotal + fee
      combinedTotal += orderTotal

      const feeRow = group.querySelector('[data-role="delivery-fee-row"]')
      const feeLabel = group.querySelector('[data-role="delivery-fee"]')
      const totalLabel = group.querySelector('[data-role="group-total"]')
      const zoneNote = group.querySelector('[data-role="out-of-city-cod-note"]')
      const rulesNote = group.querySelector('[data-role="cod-rules-note"]')
      if (feeRow) feeRow.classList.toggle('hidden', !delivery)
      if (feeLabel) feeLabel.textContent = fee > 0 ? this.formatPkr(fee) : 'Free'
      if (totalLabel) totalLabel.textContent = this.formatPkr(orderTotal)
      if (zoneNote) zoneNote.classList.toggle('hidden', !zoneBlocksCod)
      if (rulesNote) {
        let note = rulesReason || ''
        if (riderBlocksCod) {
          note = 'First orders with Ebazzari Rider need advance payment. Use shop delivery for COD, or pay advance with Rider.'
        }
        rulesNote.textContent = note
        rulesNote.classList.toggle('hidden', !(rulesBlockCod || riderBlocksCod) || !note)
      }
    })

    if (this.hasSharedDeliveryTarget) {
      this.sharedDeliveryTarget.classList.toggle('hidden', !anyDelivery)
    }
    if (this.hasAddressInputTarget) {
      this.addressInputTarget.required = anyDelivery
    }
    if (this.hasCodWarningTarget) {
      this.codWarningTarget.classList.toggle('hidden', !blockedByCod)
    }
    if (this.hasAggregateTotalTarget) {
      this.aggregateTotalTarget.textContent = this.formatPkr(combinedTotal)
    }
    if (this.hasSubmitButtonTarget) {
      const addressMissing = anyDelivery && !this.addressInputTarget?.value.trim()
      this.submitButtonTarget.disabled =
        !this.phonePresentValue || blockedByCod || configurationMissing || addressMissing
    }
  }

  advancedCodRulesReason (group) {
    if (group.dataset.advancedCodRules !== 'true') return null

    const amount = Number(group.dataset.subtotalCents || 0)
    const minCents = Number(group.dataset.codMinOrderCents || 0)
    const maxCents = Number(group.dataset.codMaxOrderCents || 0)

    if (minCents > 0 && amount < minCents) {
      return `COD starts from ${this.formatPkr(minCents)}. Add more items or choose advance payment.`
    }
    if (maxCents > 0 && amount > maxCents) {
      return `COD is limited to ${this.formatPkr(maxCents)}. Choose advance payment for larger orders.`
    }
    if (group.dataset.buyerIsNew !== 'true') return null

    if (group.dataset.codBlockNewBuyers === 'true') {
      return 'First-time buyers at this shop need advance payment (COD off for new buyers).'
    }

    const newMax = Number(group.dataset.codNewBuyerMaxCents || 0)
    if (newMax > 0 && amount > newMax) {
      return `First-time buyers can use COD up to ${this.formatPkr(newMax)}. Choose advance payment or a smaller cart.`
    }

    return null
  }

  syncGroupZone (group, fulfillment) {
    const zoneBlock = group.querySelector('[data-role="delivery-zone"]')
    if (!zoneBlock) return
    zoneBlock.classList.toggle('hidden', fulfillment !== 'shop_delivery')
  }

  syncGroupCod (group, codBlocked) {
    group.querySelectorAll('[data-role="cod-option"]').forEach((option) => {
      const input = option.querySelector('input[type="radio"]')
      if (!input) return

      const accountBlocked = !this.codAllowedValue
      const blocked = accountBlocked || codBlocked
      if (blocked && input.checked) {
        input.checked = false
        const fallback = group.querySelector('input[data-role="payment"]:not(:disabled)')
        if (fallback && fallback !== input) fallback.checked = true
        // Prefer a non-COD option if the first :not(:disabled) is still this COD input.
        if (!fallback || fallback === input || fallback.value === 'cod') {
          const other = Array.from(group.querySelectorAll('input[data-role="payment"]'))
            .find((candidate) => candidate !== input && candidate.value !== 'cod' && !candidate.disabled)
          if (other) other.checked = true
        }
      }

      input.disabled = blocked
      option.classList.toggle('opacity-55', blocked)
      option.classList.toggle('cursor-not-allowed', blocked)
      option.classList.toggle('cursor-pointer', !blocked)
      option.classList.toggle('hidden', !!codBlocked)
    })
  }

  selectedZone (group) {
    const selected = group.querySelector('input[data-role="delivery-zone"]:checked')
    return selected?.value || 'local'
  }

  formatPkr (cents) {
    return `PKR ${(Number(cents || 0) / 100).toLocaleString('en-PK', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`
  }
}
